import { coinIcon } from "@/utils/coin-icon";
import classNames from "classnames";
import { useTranslations } from "use-intl";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/store";
import {
  setStatisticsCurrency,
  setStatisticsPortfolio,
  toggleStatisticsSelection,
} from "@/store/sub_accounts";
import { useSubAccountsByCurrency } from "@/hooks";
import style from "./index.module.scss";

interface IProps {
  // A view was picked ("Все активы", "Все BTC" or a tick): the dashboard
  // shows it, so the caller switches to the statistics page. `done` is false
  // for a tick, so a dropdown can stay open while several are ticked.
  onViewChange?: (done: boolean) => void;
  // A single sub-account was picked by its name: the caller decides what
  // that means on the current page.
  onSubAccountPick: (name: string, currency: string) => void;
  // Narrows the tree (search box of the "Просмотр" dropdown).
  filter?: string;
}

// The "Просмотр" tree: all assets -> one currency -> its sub-accounts.
// Ticking several sub-accounts shows their sum; a selection stays within one
// currency because the figures are in that coin.
export function CurrencySidebar({ onViewChange, onSubAccountPick, filter = "" }: IProps) {
  const i18n = useTranslations("cabinet.statistics");
  const dispatch = useAppDispatch();
  const groups = useSubAccountsByCurrency();
  const { statisticsCurrency, statisticsViewMode, statisticsSelection, currentSubAccount } =
    useSelector((state: RootState) => state.subAccount);
  const needle = filter.trim().toLowerCase();

  return (
    <div className={style.sidebar}>
      <button
        type="button"
        className={classNames(
          style.sidebar__item,
          style.sidebar__item__allAssets,
          statisticsViewMode === "portfolio" && style.active,
        )}
        onClick={() => {
          dispatch(setStatisticsPortfolio());
          onViewChange?.(true);
        }}
      >
        {i18n("allAssets")}
      </button>
      {groups.map((group) => {
        const accounts = group.subAccounts.filter((s) =>
          s.name.toLowerCase().includes(needle),
        );
        if (needle && !accounts.length) {
          return null;
        }
        const inScope =
          statisticsViewMode === "aggregate" && statisticsCurrency === group.currency;
        return (
          <div className={style.sidebar__group} key={group.currency}>
            <div className={style.sidebar__group__heading}>
              {coinIcon(group.currency) && (
                <img
                src={coinIcon(group.currency)}
                alt={group.currency}
              />
              )}
              <span>{group.currency}</span>
            </div>
            <button
              type="button"
              className={classNames(
                style.sidebar__item,
                inScope && !statisticsSelection.length && style.active,
              )}
              onClick={() => {
                dispatch(setStatisticsCurrency(group.currency));
                onViewChange?.(true);
              }}
            >
              {i18n("allOf")} {group.currency}
            </button>
            {accounts.map((subAccount) => {
              const ticked = inScope && statisticsSelection.includes(subAccount.name);
              return (
                <div
                  key={subAccount.id}
                  className={classNames(
                    style.sidebar__item,
                    style.sidebar__item__nested,
                    (ticked ||
                      (statisticsViewMode === "single" &&
                        currentSubAccount === subAccount.name)) &&
                      style.active,
                  )}
                >
                  <input
                    type="checkbox"
                    className={style.sidebar__check}
                    checked={ticked}
                    title={i18n("selectSeveral")}
                    onChange={() => {
                      dispatch(
                        toggleStatisticsSelection({
                          name: subAccount.name,
                          currency: group.currency,
                        }),
                      );
                      onViewChange?.(false);
                    }}
                  />
                  <span
                    className={style.sidebar__name}
                    onClick={() => onSubAccountPick(subAccount.name, group.currency)}
                  >
                    {subAccount.name}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
